import { captureException } from "@sentry/react";
import { ApiError, ApiErrorData } from "@steto-care/steto-query";
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { z } from "zod/v4-mini";

import { Schema, cz } from "@/utils/zodUtils.ts";

const AXIOS_NOT_INITIALIZED_ERROR = "AXIOS_NOT_INITIALIZED";
const RESPONSE_VALIDATION_ERROR = "RESPONSE_VALIDATION_FAILED";
const UNKNOWN_ERROR = "UNKNOWN_ERROR";

const apiErrorSchema = (statusCode: number): Schema<ApiErrorData> =>
  z.pipe(
    z.object({
      error: z.string(),
      data: z.unknown(),
    }),
    z.transform(data => ({
      ...data,
      statusCode,
    })),
  );

export class BaseApiDatasource {
  axiosInstance: AxiosInstance | undefined;

  async request<T>(method: Method, url: string, schema: Schema<T>, config?: AxiosRequestConfig): Promise<T> {
    if (!this.axiosInstance) {
      captureException(AXIOS_NOT_INITIALIZED_ERROR); // Capture when axios instance is not initialized
      throw new ApiError({
        statusCode: 500,
        error: AXIOS_NOT_INITIALIZED_ERROR,
      });
    }
    let response: AxiosResponse<unknown>;
    try {
      response = await this.axiosInstance.request({
        ...config,
        method,
        url,
      });
    } catch (e) {
      if (e instanceof AxiosError && e.response) {
        // Parse Axios error
        const errorResult = cz.safeParse(apiErrorSchema(e.response.status))(e.response.data);
        if (errorResult.status === "success") {
          throw new ApiError(errorResult.data);
        } else {
          captureException(e);
          captureException(errorResult.error);
          throw new ApiError({
            statusCode: e.response.status,
            error: RESPONSE_VALIDATION_ERROR,
            data: errorResult.error,
          });
        }
      } else {
        captureException(e);
        throw new ApiError({
          statusCode: 500,
          error: UNKNOWN_ERROR,
        });
      }
    }
    const result = cz.safeParse(schema)(response.data);
    if (result.status === "success") {
      return result.data;
    } else {
      console.warn(result.error);
      captureException(result.error);
      throw new ApiError({
        statusCode: 400,
        error: RESPONSE_VALIDATION_ERROR,
        data: result.error,
      });
    }
  }

  get = <T>(url: string, schema: Schema<T>, config?: AxiosRequestConfig): Promise<T> =>
    this.request("get", url, schema, config);

  post = <T>(url: string, schema: Schema<T>, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    this.request("post", url, schema, {
      ...config,
      data,
    });

  put = <T>(url: string, schema: Schema<T>, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    this.request("put", url, schema, {
      ...config,
      data,
    });

  patch = <T>(url: string, schema: Schema<T>, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    this.request("patch", url, schema, {
      ...config,
      data,
    });

  delete = <T>(url: string, schema: Schema<T>, config?: AxiosRequestConfig): Promise<T> =>
    this.request("delete", url, schema, config);
}

export type BaseApiDatasourceSpec = {
  get: <T>(url: string, schema: Schema<T>, config?: AxiosRequestConfig) => Promise<T>;
  post: <T>(url: string, schema: Schema<T>, data?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  put: <T>(url: string, schema: Schema<T>, data?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  patch: <T>(url: string, schema: Schema<T>, data?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  delete: <T>(url: string, schema: Schema<T>, config?: AxiosRequestConfig) => Promise<T>;
};
